/**
 * Material Configuration for Different Visual Modes
 * Supports independent stacking of ghost mode and color mode
 */

// Base properties for each mode component
export const MODE_PROPERTIES = {
  // Ghost mode properties - affects transparency and polygon offset
  GHOST: {
    opacity: 0.5,
    transparent: true,
    polygonOffsetFactor: 2,
    description: 'Semi-transparent - see internal geometry'
  },

  // Default (non-ghost) properties
  DEFAULT_OPACITY: {
    opacity: 1.0,
    transparent: false,
    polygonOffsetFactor: -2,
    description: 'Opaque default'
  },

  // Color mode properties - affects color
  COLOR: {
    color: 0xff8e00, // orange - for contrast with blue preview materials
    vertexColors: false,
    description: 'Orange color - highlight differences from blue tools'
  },

  // Default (no color tint) properties
  NO_COLOR_TINT: {
    color: 0xffffff, // white - shows vertex colors as-is
    vertexColors: true,
    description: 'Default white - shows vertex colors'
  }
};

// Common polygon offset settings
export const POLYGON_OFFSET_BASE = {
  polygonOffset: true,
  polygonOffsetUnits: 1.0
};

/**
 * Compute material configuration based on independent ghost and color mode flags
 * @param {boolean} ghostModeEnabled - Whether ghost mode is active
 * @param {boolean} colorModeEnabled - Whether color mode is active
 * @returns {Object} Composed material configuration
 */
export const computeMaterialConfig = (ghostModeEnabled, colorModeEnabled) => {
  // Start with polygon offset base
  const config = { ...POLYGON_OFFSET_BASE };

  // Apply opacity and transparency based on ghost mode
  const opacityProps = ghostModeEnabled ? MODE_PROPERTIES.GHOST : MODE_PROPERTIES.DEFAULT_OPACITY;
  config.opacity = opacityProps.opacity;
  config.transparent = opacityProps.transparent;
  config.polygonOffsetFactor = opacityProps.polygonOffsetFactor;

  // Apply color based on color mode
  const colorProps = colorModeEnabled ? MODE_PROPERTIES.COLOR : MODE_PROPERTIES.NO_COLOR_TINT;
  config.color = colorProps.color;
  config.vertexColors = colorProps.vertexColors;

  return config;
};

/**
 * Apply computed material configuration to a material object
 * @param {THREE.Material} material - The Three.js material to update
 * @param {boolean} ghostModeEnabled - Whether ghost mode is active
 * @param {boolean} colorModeEnabled - Whether color mode is active
 */
export const applyMaterialModes = (material, ghostModeEnabled, colorModeEnabled) => {
  if (!material) {
    console.warn(`Material is null or undefined`);
    return;
  }

  const config = computeMaterialConfig(ghostModeEnabled, colorModeEnabled);

  material.opacity = config.opacity;
  material.transparent = config.transparent;
  material.vertexColors = config.vertexColors;
  material.color.set(config.color);
  material.polygonOffset = config.polygonOffset;
  material.polygonOffsetFactor = config.polygonOffsetFactor;
  material.polygonOffsetUnits = config.polygonOffsetUnits;
  material.needsUpdate = true;
};
